import React from 'react';
import AnimatedSection from '../components/ui/AnimatedSection';
import { Star, Briefcase } from 'react-feather';
import AuthorityBurst from '../components/funnel/AuthorityBurst';
import CTABand from '../components/funnel/CTABand';
import Seo from '../components/shared/Seo';
import FunnelHero from '../components/funnel/FunnelHero';
import EpiphanyBridgeContent from '../components/funnel/EpiphanyBridgeContent';
import MechanismDiagram from '../components/funnel/MechanismDiagram';
import OfferStack from '../components/funnel/OfferStack';
import FaqSection from '../components/blog/FaqSection';
import type { OfferStackItem, MechanismStep, ContentBlock, FAQ } from '../types';
import { generateFaqSchema, generateProfilePageSchema } from '../lib/schema';
import FlippableCard from '../components/ui/FlippableCard';
import PersonAvatar from '../components/ui/PersonAvatar';
import { useData } from '../context/DataContext';
import LocalizedLink from '../components/ui/LocalizedLink';
import { resolvePracticeAreaPath } from '../lib/utils';
import { useTranslation } from 'react-i18next';


const AboutPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { practiceAreas, firmStats, commonOfferStack, commonRiskReversal, authors, testimonials, caseResults } = useData();

  // Load page-specific content from translation files
  const epiphanyBridgeContent = t('aboutPage.epiphanyBridgeContent', { returnObjects: true }) as ContentBlock[];
  const mechanism = t('aboutPage.mechanism', { returnObjects: true }) as { title: string; subtitle: string; steps: MechanismStep[] };
  const offerStack: OfferStackItem[] = commonOfferStack;
  const riskReversal = commonRiskReversal;
  const faqs = t('aboutPage.faqs', { returnObjects: true }) as FAQ[];

  const foundingAttorney = authors.find(a => a.id === 'christian-burridge');
  const faqSchema = generateFaqSchema(faqs);
  const profilePageSchema = foundingAttorney ? generateProfilePageSchema(foundingAttorney) : null;
  const pageSchemas = [faqSchema, profilePageSchema].filter(Boolean);
  const proofResult = caseResults[0];
  const proofTestimonial = testimonials[0];

  return (
    <>
      <Seo
          title={t('aboutPage.seoTitle')}
          description={t('aboutPage.seoDescription')}
          schema={pageSchemas}
      />
      <div>
        <FunnelHero
          headline={t('aboutPage.heroHeadline')}
          subheadline={t('aboutPage.heroSubheadline')}
          imageUrl="https://images.unsplash.com/photo-1590650516494-0c8e4a4dd67e?q=80&w=2071&auto=format&fit=crop&fm=webp"
        />
        
        <AuthorityBurst stats={firmStats} />

        <div className="py-24 bg-brand-dark">
          <AnimatedSection>
              <EpiphanyBridgeContent content={epiphanyBridgeContent} />
          </AnimatedSection>
        </div>

        <MechanismDiagram mechanism={mechanism} />
        
        {/* Attorney Profiles Section - Hidden for now as requested by user
        <AnimatedSection className="py-24 bg-brand-dark">
          <div className="mx-auto max-w-7xl px-6">
             <div className="mx-auto mb-16 max-w-3xl text-center">
                 <h2 className="text-4xl md:text-5xl font-serif font-bold">Meet Our Team</h2>
                 <p className="mt-4 text-lg text-white/70">Dedicated advocates fighting for your rights.</p>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
               {authors.filter(a => a.id !== 'the-action-firm-legal-team').map(attorney => (
                 <LocalizedLink 
                   key={attorney.id} 
                   to={`/attorneys/${attorney.id}`}
                   className="group block rounded-2xl border border-white/10 bg-black/50 overflow-hidden hover:border-brand-gold/50 transition-colors"
                 >
                   <div className="aspect-[3/4] overflow-hidden">
                     <PersonAvatar name={attorney.name} imageUrl={attorney.imageUrl} className="w-full h-full transition-transform duration-500 group-hover:scale-105" initialsClassName="text-6xl" />
                   </div>
                   <div className="p-6">
                     <h3 className="text-2xl font-serif font-bold mb-1 group-hover:text-brand-gold transition-colors">{attorney.name}</h3>
                     <p className="text-brand-gold-light text-sm mb-4">{attorney.title}</p>
                     <p className="text-white/70 line-clamp-3">{attorney.bio}</p>
                     <div className="mt-4 inline-flex items-center text-sm font-semibold text-brand-gold group-hover:text-brand-gold-light">
                       View Profile <span className="ml-2">→</span>
                     </div>
                   </div>
                 </LocalizedLink>
               ))}
             </div>
          </div>
        </AnimatedSection>
        */}

        <OfferStack offerStack={offerStack} />
        
        {/* Proof Wall */}
        <AnimatedSection className="py-24">
          <div className="mx-auto max-w-7xl px-6">
             <div className="mx-auto mb-16 max-w-3xl text-center">
                 <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('aboutPage.proofTitle')}</h2>
                 <p className="mt-4 text-lg text-white/70">{t('aboutPage.proofSubtitle')}</p>
             </div>
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
                <FlippableCard
                    frontContent={
                        <div className="flex h-full flex-col justify-center rounded-3xl border border-white/10 bg-brand-dark p-8 shadow-xl glass-effect">
                            <h3 className="text-2xl font-serif font-semibold text-center mb-4">{t('aboutPage.proofTestimonialTitle')}</h3>
                            <p className="text-xl leading-relaxed text-white/90 italic text-center">“{proofTestimonial?.quote || 'Excellent service and results.'}”</p>
                        </div>
                    }
                    backContent={
                        <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-white/10 bg-black/80 p-8 shadow-xl glass-effect text-center">
                           <div className="text-3xl font-bold gold-gradient-text">{proofResult?.amount || 'Confidential'}</div>
                           <div className="mt-2 text-md text-white/80">{proofResult?.caseType || 'Successful Settlement'}</div>
                       </div>
                    }
                />

                <FlippableCard
                    frontContent={
                        <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-yellow-400/10 to-transparent p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                            <h3 className="text-2xl font-serif font-semibold text-center mb-4">{t('aboutPage.proofResultTitle')}</h3>
                            <p className="text-lg tracking-wide text-white/80 text-center">{proofResult?.caseType || 'Successful Settlement'}</p>
                            <div className="text-6xl font-extrabold gold-gradient-text mt-2 text-center">{proofResult?.amount || 'Confidential'}</div>
                        </div>
                    }
                    backContent={
                        <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-tr from-yellow-400/20 to-brand-dark/90 p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                            {proofResult?.practiceAreaSlug && (
                                <LocalizedLink to={resolvePracticeAreaPath(proofResult.practiceAreaSlug, practiceAreas.map((p) => p.slug))} className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 transition-colors rounded-full px-4 py-2 bg-white/10 border border-white/10 hover:bg-white/20 glass-effect">
                                    <Briefcase size={16} /> {t('aboutPage.proofSeeMethodButton')}
                                </LocalizedLink>
                            )}
                        </div>
                    }
                />
             </div>
          </div>
        </AnimatedSection>

        <div className="py-24 bg-white/5">
            <FaqSection 
              faqs={faqs}
              title={t('aboutPage.faqTitle')}
            />
         </div>

         <AnimatedSection className="py-24">
           <div className="mx-auto max-w-4xl px-6 text-center">
              <h2 className="text-4xl md:text-5xl font-serif font-bold">{riskReversal.title}</h2>
              <p className="mt-6 text-lg text-white/80">{riskReversal.description}</p>
           </div>
         </AnimatedSection>
        
        <CTABand
          headline={t('aboutPage.ctaBandHeadline')}
          subheadline={t('aboutPage.ctaBandSubheadline')}
          buttonText={t('aboutPage.ctaBandButton')}
          buttonLink="/contact"
          isPrimary={true}
        />
      </div>
    </>
  );
};

export default AboutPage;